class CompareUtils {
  static cookieName = 'hypertheme:compare-products';
  static memoryProducts = [];
  static storageUnavailable = false;

  /**
   *
   * @returns {array}
   */
  static getSelectedProducts() {
    let products = this.memoryProducts;
    if (!this.storageUnavailable && FoxTheme.config.hasLocalStorage) {
      try { products = FoxTheme.utils.getStorage(this.cookieName) || []; }
      catch { this.storageUnavailable = true; }
    }
    if (!Array.isArray(products)) products = [];
    const seen = new Set();
    return products.flatMap(product => {
      if (!product || !/^\d+$/.test(String(product.id))) return [];
      const id = String(product.id);
      const url = this.productUrl(product.url);
      if (!url || seen.has(id)) return [];
      seen.add(id);
      return [{ id, url }];
    });
  }

  static productUrl(value) {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/\/products\/[^/]+/.test(url.pathname)) return null;
      return url.pathname + url.search;
    } catch { return null; }
  }

  static setSelectedProducts(products) {
    this.memoryProducts = products;
    try {
      if (products.length > 0) FoxTheme.utils.setStorage(this.cookieName, products);
      else localStorage.removeItem(this.cookieName);
    } catch { this.storageUnavailable = true; }
  }

  static getProductsCount() {
    return this.getSelectedProducts().length;
  }

  /**
   * Check the given product id in compare list.
   * @param {*} productId
   * @returns {boolean}
   */
  static isProductInList(productId) {
    const products = this.getSelectedProducts();

    return products.some((product) => product.id === String(productId));
  }

  /**
   *
   * @param {*} productId
   * @param {*} productUrl
   * @returns {boolean} True if the product added to list.
   */
  static addToList(productId, productUrl) {
    productId = String(productId);
    productUrl = this.productUrl(productUrl);
    if (!/^\d+$/.test(productId) || !productUrl) return false;
    const products = this.getSelectedProducts();
    if (products.some(product => product.id === productId)) {
      this.updateCompareCheckboxes(products);
      return true;
    }

    if (products.length < FoxTheme.compare.maxProductsInCompare) {
      if (!products.some((product) => product.id === productId)) {
        products.push({ id: productId, url: productUrl });
      }

      this.setSelectedProducts(products);
      this.updateCompareCheckboxes(products);
      this.setActive(true);

      FoxTheme.pubsub.publish('compare:add', { products: products, productId: productId, productUrl: productUrl });

      return true;
    } else {
      alert(FoxTheme.compare.alertMessage);

      return false;
    }
  }

  static removeFromList(productId) {
    productId = String(productId);
    let products = this.getSelectedProducts();
    if (products) {
      products = products.filter((product) => product.id !== productId);
    }
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes(products);
    this.setActive(products.length > 0);
    FoxTheme.pubsub.publish('compare:remove', { products: products, productId: productId });
  }

  static clearList() {
    const products = this.getSelectedProducts();
    if (products) {
      products.forEach((product) => {
        this.removeFromList(product.id);
      });
    }
  }

  static updateCompareCheckboxes(products = null) {
    products = products || this.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = products.some((product) => product.id === String(checkbox.dataset.productId));
    });
  }

  static setActive(active) {
    document.body.classList.toggle('is-product-comparing', active);
    document.querySelectorAll('compare-switch input').forEach(input => { input.checked = active; });
    try { localStorage.setItem('hypertheme:compare-is-active', String(active)); } catch {}
    FoxTheme.pubsub.publish('compare:toggle', { isActive: active });
  }

  static getTemplateUrl(productUrl, queryObj = {}) {
    const url = new URL(productUrl, window.location.origin);

    for (const key in queryObj) {
      if (Object.prototype.hasOwnProperty.call(queryObj, key)) {
        url.searchParams.set(key, queryObj[key]);
      }
    }

    url.hash = '';
    return url.pathname + url.search;
  }

  static getCompareBarItemTemplate(productUrl) {
    return this.getTemplateUrl(productUrl, {
      sections: 'product-compare-bar-item'
    });
  }
}

class CompareSwitch extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'hypertheme:compare-is-active';

    this.classes = {
      isActive: 'is-product-comparing'
    };

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.checkboxEl = this.querySelector('input');
    if (!this.checkboxEl) return;
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl?.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    if (FoxTheme.config.hasLocalStorage) {
      const isActive = window.localStorage.getItem(this.cookieName) === 'true';
      if (isActive) {
        this.checkboxEl.checked = isActive;
        this.handleChange(isActive);
      }
    }
  }

  onChange(evt) {
    evt.preventDefault();

    this.handleChange(this.checkboxEl.checked);
  }

  handleChange(isActive) {
    CompareUtils.setActive(isActive);
  }
}
customElements.define('compare-switch', CompareSwitch);

class CompareCheckbox extends HTMLElement {
  constructor() {
    super();

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.checkboxEl = this.querySelector('input');
    if (!this.checkboxEl) return;
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl?.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    const { productId } = this.checkboxEl.dataset;
    this.checkboxEl.checked = CompareUtils.isProductInList(productId);
  }

  onChange(evt) {
    evt.preventDefault();

    const { productId } = this.checkboxEl.dataset;
    let productUrl = this.checkboxEl.dataset.productUrl;
    const variant = this.closest('product-info')?.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (variant && /^\d+$/.test(variant.value)) {
      productUrl = CompareUtils.getTemplateUrl(productUrl, { variant: variant.value });
    }

    if (this.checkboxEl.checked) {
      const isAdded = CompareUtils.addToList(productId, productUrl);
      if (!isAdded) {
        this.checkboxEl.checked = false;
      }
    } else {
      CompareUtils.removeFromList(productId);
    }
  }
}
customElements.define('compare-checkbox', CompareCheckbox);

class CompareRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.clickHandler = this.onClick.bind(this);
  }

  connectedCallback() { this.addEventListener('click', this.clickHandler); }

  onClick(event) {
    event.preventDefault();

    const { productId } = this.dataset;
    const { target } = event;

    CompareUtils.removeFromList(productId);

    const compareModal = target.closest('.compare-drawer');
    if (compareModal) {
      compareModal.updateProductsCount();
    }

    if (CompareUtils.getProductsCount() < 2) {
      compareModal && compareModal.hide();
    } else if (compareModal) compareModal.renderCompareTable();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }
}
customElements.define('compare-remove', CompareRemove, { extends: 'button' });

class CompareBar extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      openBtn: '.compare-bar__open',
      clearBtns: ['.compare-bar__clear'],
      toggleList: '.compare-bar__toggle-list',
      list: '.compare-bar__list',
      counter: '.compare-bar__counter'
    };

    this.classes = {
      listVisible: 'is-show-list'
    };

    this.abortController = new AbortController();
    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    this.atLeastItemToShow = Math.min(5, FoxTheme.compare.maxProductsInCompare); // Total compare products and placeholders.
    this.renderVersion = 0;
  }

  bindEvents() {
    this.abortController = new AbortController();
    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);

    this.elements.clearBtns.forEach((clearBtn) => {
      clearBtn.addEventListener('click', this.handleClear.bind(this), {
        signal: this.abortController.signal
      });
    });

    this.elements.toggleList.addEventListener('click', this.handleToggleList.bind(this), {
      signal: this.abortController.signal
    });

    this.itemAddUnsubscriber = FoxTheme.pubsub.subscribe('compare:add', this.onItemAdded.bind(this));
    this.itemRemoveUnsubscriber = FoxTheme.pubsub.subscribe('compare:remove', this.onItemRemoved.bind(this));
    this.querySelector('[data-compare-retry]')?.addEventListener('click', () => this.renderBar(), { signal: this.abortController.signal });
  }

  itemAddUnsubscriber = undefined;
  itemRemoveUnsubscriber = undefined;

  connectedCallback() {
    this.bindEvents();
    try {
      if (localStorage.getItem('hypertheme:compare-is-active') === 'true') CompareUtils.setActive(true);
    } catch {}
    this.renderBar();
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.addEventListener('change', this.setHeight.bind(this), { signal: this.abortController.signal });
    this.setHeight();
  }

  disconnectedCallback() {
    this.renderVersion++;
    this.abortController.abort();

    if (this.itemAddUnsubscriber) {
      this.itemAddUnsubscriber();
    }

    if (this.itemRemoveUnsubscriber) {
      this.itemRemoveUnsubscriber();
    }
  }

  onItemAdded(event) {
    this.renderBar();
  }

  onItemRemoved(event) {
    this.renderBar();
  }

  async renderBar() {
    const version = ++this.renderVersion;
    const products = CompareUtils.getSelectedProducts();
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
    const compareItemHtmls = await this.getBarItems(products);
    if (version !== this.renderVersion || !this.isConnected) return;
    this.elements.list.replaceChildren(...compareItemHtmls.map(item => item.node));
    this.addPlaceholders(products.length);
    const error = this.querySelector('[data-compare-error]');
    if (error) error.hidden = !compareItemHtmls.some(item => item.failed);
    this.setHeight();
  }

  async getBarItems(products) {
    return Promise.all(products.map(async product => {
      try {
        const response = await fetch(CompareUtils.getCompareBarItemTemplate(product.url));
        if (!response.ok) throw new Error('Compare item unavailable');
        const payload = await response.json();
        if (typeof payload['product-compare-bar-item'] !== 'string') throw new Error('Missing compare section');
        const doc = new DOMParser().parseFromString(payload['product-compare-bar-item'], 'text/html');
        const node = doc.querySelector('.compare-bar__item');
        if (!node || node.dataset.productId !== product.id) throw new Error('Compare product mismatch');
        return { node, failed: false };
      } catch {
        const node = document.createElement('li');
        node.className = 'compare-bar__item';
        node.dataset.productId = product.id;
        const button = document.createElement('button', { is: 'compare-remove' });
        button.setAttribute('is', 'compare-remove');
        button.type = 'button';
        button.dataset.productId = product.id;
        button.className = 'compare-bar__item-remove btn btn--white btn--icon';
        button.setAttribute('aria-label', this.dataset.removeLabel || 'Remove');
        button.textContent = '×';
        node.append(button);
        return { node, failed: true };
      }
    }));
  }


  handleToggleList(event) {
    event.preventDefault();

    if (this.classList.contains(this.classes.listVisible)) {
      this.classList.remove(this.classes.listVisible);
    } else {
      this.classList.add(this.classes.listVisible);
    }

    this.setHeight();
  }

  handleClear() {
    CompareUtils.clearList();
  }

  toggleCompareButton(count) {
    this.elements.openBtn.disabled = !(count > 1);
  }

  updateCounters(count) {
    this.elements.counter.innerText = count;
  }

  removePlaceholdes() {
    Array.from(this.elements.list.querySelectorAll('li')).forEach((li) => {
      if (li.classList.contains('compare-bar__item-placeholder')) {
        li.remove();
      }
    });
  }

  addPlaceholders(count) {
    const placeholderCount = this.atLeastItemToShow - count;

    for (let i = 0; i < placeholderCount; i++) {
      const placeholderItem = document.createElement('li');

      placeholderItem.classList.add('compare-bar__item', 'compare-bar__item-placeholder');
      this.elements.list.appendChild(placeholderItem);
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      document.documentElement.style.setProperty('--compare-bar-height', `${offsetHeight}px`);
    });
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      table: '.compare-table'
    };

    this.classesToRemoveOnLoad = 'drawer--loading';
    this.addEventListener('click', event => {
      if (event.target.closest('[data-compare-retry]')) this.renderCompareTable();
    });
  }

  get requiresBodyAppended() {
    return false;
  }

  get selector() {
    return '.product-comparison-table';
  }

  get sourceSelector() {
    return '#MainProduct-compare__rows';
  }

  get placeholderSelector() {
    return '#MainProduct-compare__placeholder';
  }

  getSectionId() {
    let sectionId = FoxTheme.ProductCompareSectionId || false;

    if (!sectionId) {
      // Get section id from overlay groups.
      const sectionEl = document.querySelector('.section-group-overlay-product-compare');
      if (sectionEl) {
        sectionId = FoxTheme.utils.getSectionId(sectionEl);
      }

      // Cache for better performance.
      FoxTheme.ProductCompareSectionId = sectionId;
    }

    return sectionId;
  }

  // shouleBeShow() {
  //   const sectionId = this.getSectionId();
  //   return typeof sectionId === 'string';
  // }

  prepareToShow() {
    super.prepareToShow();
    this.renderCompareTable();
  }

  async renderCompareTable() {
    const version = this.renderVersion = (this.renderVersion || 0) + 1;
    const products = CompareUtils.getSelectedProducts();
    this.classList.add(this.classesToRemoveOnLoad);
    const error = this.querySelector('[data-compare-error]');
    if (error) error.hidden = true;
    this.updateProductsCount();
    this.querySelectorAll('.compare-table > tbody > tr[data-compare-spec-row]').forEach(row => row.remove());
    const tableRows = this.querySelectorAll('.compare-table > tbody > tr');
    tableRows.forEach(row => row.querySelectorAll('td:not(.td-label)').forEach(cell => cell.remove()));
    try {
      let compareRows = await this.getCompareTableRows(products);
      if (version !== this.renderVersion || !this.isConnected) return;
      if (Shopify.designMode && products.length === 0) {
        compareRows = this.getCompareTablePlaceholderRows(3);
        this.updateProductsCount(3);
      }
      const columns = compareRows.map(compareRow => [...tableRows].map(row => {
        const cell = compareRow.querySelector('td[data-compare-key="' + row.dataset.compareKey + '"]');
        if (!cell) throw new Error('Missing compare row');
        return cell;
      }));
      columns.forEach(cells => cells.forEach((cell, index) => tableRows[index].appendChild(cell)));
      const specs = new Map();
      compareRows.forEach(fragment => fragment.querySelectorAll('td[data-compare-spec]').forEach(cell => {
        specs.set(cell.dataset.compareKey, { label: cell.dataset.label, order: Number(cell.dataset.order) });
      }));
      const body = this.querySelector('.compare-table > tbody');
      [...specs].sort((a, b) => a[1].order - b[1].order).forEach(([key, spec]) => {
        const row = document.createElement('tr');
        row.dataset.compareSpecRow = '';
        row.dataset.compareKey = key;
        const label = document.createElement('td');
        label.className = 'td-label background-1 text-body';
        label.textContent = spec.label;
        row.append(label);
        compareRows.forEach(fragment => {
          const cell = [...fragment.querySelectorAll('td[data-compare-spec]')].find(cell => cell.dataset.compareKey === key);
          if (cell) row.append(cell);
          else { const missing = document.createElement('td'); missing.textContent = '—'; row.append(missing); }
        });
        body.append(row);
      });
    } catch {
      if (version === this.renderVersion && error) error.hidden = false;
    } finally {
      if (version === this.renderVersion) this.classList.remove(this.classesToRemoveOnLoad);
    }
  }

  prepareToHide() {
    super.prepareToHide();
    this.renderVersion = (this.renderVersion || 0) + 1;
  }

  updateProductsCount(count) {
    this.style.setProperty('--number-items', count ?? CompareUtils.getProductsCount());
  }

  async getCompareTableRows(products) {
    const sectionId = this.getSectionId();
    if (!sectionId) throw new Error('Compare section unavailable');
    return Promise.all(products.map(async product => {
      const response = await fetch(CompareUtils.getTemplateUrl(product.url, { section_id: sectionId }));
      if (!response.ok) throw new Error('Compare product unavailable');
      const text = await response.text();
      const template = new DOMParser().parseFromString(text, 'text/html').querySelector(this.sourceSelector);
      if (!template || template.dataset.productId !== product.id) throw new Error('Compare product mismatch');
      return template.content.cloneNode(true);
    }));
  }

  getCompareTablePlaceholderRows(count) {
    const rowTemplate = this.querySelector(this.placeholderSelector);

    const returns = [];
    for (let i = 0; i < count; i++) {
      returns.push(rowTemplate.content.cloneNode(true));
    }

    return returns;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleAfterHide();
  }

  handleAfterHide() {
    super.handleAfterHide();
    this.renderVersion = (this.renderVersion || 0) + 1;
    // Clean the table.
    this.querySelectorAll('.compare-table > tbody > tr').forEach((row) => {
      const nonLabelCells = row.querySelectorAll('td:not(.td-label)');
      nonLabelCells.forEach((cell) => {
        cell.remove();
      });
    });

    this.classList.add(this.classesToRemoveOnLoad);
  }
}
customElements.define('compare-drawer', CompareDrawer);
